/* Veyro: Nova Poshta selection. No credentials, no automatic write retries. */
(() => {
  const TAG = 'veyro-np-selector';
  const root = () => window.Shopify?.routes?.root || '/';
  const TXT = {
    city: 'Оберіть місто зі списку.', branch: 'Оберіть відділення зі списку.',
    unavailable: 'Сервіс Нової пошти тимчасово недоступний. Спробуйте ще раз.',
    invalid: 'Не вдалося перевірити відповідь сервісу. Спробуйте ще раз.'
  };
  async function request(url, options = {}, parentSignal) {
    const controller = new AbortController();
    const abort = () => controller.abort();
    if (parentSignal?.aborted) controller.abort();
    else parentSignal?.addEventListener('abort', abort, { once: true });
    const timer = setTimeout(abort, 15000);
    try {
      const response = await fetch(url, { ...options, signal: controller.signal });
      let data;
      try { data = await response.json(); } catch { throw new Error(TXT.invalid); }
      if (!response.ok) throw new Error(data?.error === 'warehouse_not_available'
        ? 'Це відділення більше не доступне. Оберіть інше.' : TXT.unavailable);
      return data;
    } finally {
      clearTimeout(timer);
      parentSignal?.removeEventListener('abort', abort);
    }
  }
  // A delivery preference belongs to this browser tab/customer, not a mutable cart token.
  const draftKey = 'veyro.np.delivery-draft.v3';
  const legacyDraftKey = 'veyro.np.delivery-draft.v2';
  const draftMemory = new Map();
  let pending = false;
  const activeCartUpdates = new Set();
  let checkoutFrame;
  function syncCheckoutButtons() {
    if (pending || activeCartUpdates.size) return;
    for (const button of document.querySelectorAll('[data-np-checkout]')) {
      const scope = button.closest?.('dialog, main') || document;
      const widgets = Array.from(scope.querySelectorAll(TAG));
      if (widgets.some(widget => widget.started && widget.isConnected)) button.disabled = false;
    }
  }
  function scheduleCheckoutSync() {
    if (checkoutFrame !== undefined) return;
    checkoutFrame = requestAnimationFrame(() => { checkoutFrame = undefined; syncCheckoutButtons(); });
  }
  class NPSelector extends HTMLElement {
    draftOwner() { return this.dataset.draftOwner || 'guest'; }
    readDraft() {
      const owner = this.draftOwner();
      let value = draftMemory.get(owner);
      if (!value) {
        try {
          value = JSON.parse(window.sessionStorage.getItem(draftKey) || 'null');
          if (!value && owner === 'guest') value = JSON.parse(window.sessionStorage.getItem(legacyDraftKey) || 'null');
        } catch { return null; }
      }
      if (!value || ![2, 3].includes(value.version) || (value.version === 3 ? value.owner !== owner : owner !== 'guest') || !Number.isFinite(value.at) || Date.now() - value.at > 12 * 60 * 60 * 1000 || value.at > Date.now() + 60000) return null;
      const text = item => typeof item === 'string' && item.length <= 500;
      if (!text(value.cityText) || !text(value.branchText)) return null;
      if (value.city !== null && (!value.city || !text(value.city.ref) || !value.city.ref || !text(value.city.name) || !value.city.name)) return null;
      if (value.warehouse !== null && (!value.city || !value.warehouse || !text(value.warehouse.ref) || !value.warehouse.ref || !text(value.warehouse.name) || !value.warehouse.name)) return null;
      return value;
    }
    persistDraft() {
      if (!this.started) return;
      const owner = this.draftOwner();
      const value = {
        version: 3, owner, at: Date.now(),
        city: this.city ? { ref: this.city.ref, name: this.city.name } : null,
        warehouse: this.warehouseRef ? { ref: this.warehouseRef, name: this.branchInput.value } : null,
        cityText: this.cityInput.value, branchText: this.branchInput.value
      };
      draftMemory.delete(owner);
      draftMemory.set(owner, value);
      while (draftMemory.size > 4) draftMemory.delete(draftMemory.keys().next().value);
      try { window.sessionStorage.setItem(draftKey, JSON.stringify(value)); } catch { /* In-page memory still survives section replacement. */ }
    }
    restoreDraft() {
      const draft = this.readDraft();
      if (!draft || this.busy) return false;
      this.cityVersion++; this.branchVersion++;
      this.cityRequest?.abort(); this.branchRequest?.abort();
      clearTimeout(this.cityTimer); clearTimeout(this.branchTimer);
      this.city = draft.city ? { ...draft.city } : null;
      this.warehouseRef = draft.warehouse?.ref || '';
      this.cityInput.value = draft.cityText;
      this.branchInput.value = draft.branchText;
      this.citySelect.replaceChildren(new Option(TXT.city, ''));
      this.branchSelect.replaceChildren(new Option(TXT.branch, ''));
      if (this.city) this.citySelect.add(new Option(this.cityInput.value || this.city.name, this.city.ref, true, true));
      if (draft.warehouse) this.branchSelect.add(new Option(draft.warehouse.name, draft.warehouse.ref, true, true));
      this.citySelect.value = this.city?.ref || '';
      this.branchSelect.value = this.warehouseRef;
      this.branchInput.disabled = !this.city;
      this.branchSelect.disabled = !this.city;
      this.more.hidden = true; this.more.disabled = false;
      this.cityInput.removeAttribute('aria-busy'); this.branchInput.removeAttribute('aria-busy');
      this.hideOptions('city'); this.hideOptions('branch');
      this.message(this.warehouseRef ? 'Ваш вибір відновлено. Перед оформленням повторно перевіримо відділення.' : this.city ? TXT.branch : TXT.city);
      return true;
    }
    connectedCallback() {
      if (this.started) return;
      if (!this.mountObserver) {
        this.mountObserver = new MutationObserver(() => {
          if (!this.isConnected) return;
          if (this.started && this.cityInput === this.querySelector('[data-np-city-search]') && this.branchInput === this.querySelector('[data-np-branch-search]') && this.citySelect === this.querySelector('[data-np-city-select]') && this.branchSelect === this.querySelector('[data-np-branch-select]') && this.more === this.querySelector('[data-np-more]') && this.status === this.querySelector('[data-np-status]')) {
            // Observing our own list/status mutations must never restore, cancel reads,
            // or rewrite the same children again. Native morph preservation keeps this subtree intact.
            return;
          }
          if (this.started) this.disconnectedCallback();
          this.connectedCallback();
        });
        this.mountObserver.observe(this, {childList:true,subtree:true});
      }
      this.cityInput = this.querySelector('[data-np-city-search]');
      this.branchInput = this.querySelector('[data-np-branch-search]');
      this.citySelect = this.querySelector('[data-np-city-select]');
      this.branchSelect = this.querySelector('[data-np-branch-select]');
      this.more = this.querySelector('[data-np-more]');
      this.status = this.querySelector('[data-np-status]');
      if (!this.cityInput || !this.branchInput || !this.citySelect || !this.branchSelect || !this.status || !this.more) {
        this.started = false; return;
      }
      this.started = true;
      this.life = new AbortController();
      this.cityVersion = (this.cityVersion || 0) + 1; this.branchVersion = (this.branchVersion || 0) + 1; this.page = 1;
      this.city = null; this.warehouseRef = ''; this.busy = false;
      const signal = this.life.signal;
      this.setupPicker('city', this.cityInput, this.citySelect);
      this.setupPicker('branch', this.branchInput, this.branchSelect);
      if (this.dataset.cityRef && this.dataset.cityName) {
        this.city = { ref: this.dataset.cityRef, name: this.dataset.cityName };
        this.cityInput.value = this.city.name;
        this.citySelect.add(new Option(this.city.name, this.city.ref, true, true));
        this.branchInput.disabled = false;
        if (this.dataset.warehouseRef && this.dataset.warehouseName) {
          this.warehouseRef = this.dataset.warehouseRef;
          this.branchInput.value = this.dataset.warehouseName;
          this.branchSelect.add(new Option(this.dataset.warehouseName, this.warehouseRef, true, true));
          this.branchSelect.disabled = false;
          this.message('Збережене відділення буде повторно перевірено перед оформленням.');
        }
      }
      this.restoreDraft();
      this.closest?.('theme-drawer')?.addEventListener('theme-drawer:open', () => {
        if (this.isConnected && this.started && !this.busy) this.restoreDraft();
      }, { signal });
      // Browser Back may restore a frozen page rather than reconnecting elements.
      window.addEventListener?.('pageshow', () => {
        if (this.isConnected && this.started && !this.busy) this.restoreDraft();
      }, { signal });
      document.addEventListener('shopify:cart:lines-update', event => {
        const life = this.life, cityVersion = this.cityVersion, branchVersion = this.branchVersion;
        Promise.resolve(event.promise).then(() => {
          if (!this.isConnected || this.life !== life || life.signal.aborted || this.busy || this.cityVersion !== cityVersion || this.branchVersion !== branchVersion) return;
          cancelAnimationFrame(this.restoreFrame);
          this.restoreFrame = requestAnimationFrame(() => {
            this.restoreFrame = undefined;
            if (!this.isConnected || this.life !== life || life.signal.aborted || this.busy || this.cityVersion !== cityVersion || this.branchVersion !== branchVersion) return;
            const draft = this.readDraft();
            if (draft && (this.cityInput.value !== draft.cityText || this.branchInput.value !== draft.branchText || (draft.warehouse && (this.branchInput.disabled || this.branchSelect.disabled)))) this.restoreDraft();
          });
        }).catch(() => { /* Native cart reports its own failures. No retry or extra cart write. */ });
      }, { signal });
      this.cityInput.addEventListener('input', () => {
        this.city = null; this.cityVersion++;
        this.cityRequest?.abort(); this.resetBranches();
        this.citySelect.replaceChildren(new Option(TXT.city, ''));
        this.showOptions('city');
        clearTimeout(this.cityTimer);
        if (this.cityInput.value.trim().length < 2) {
          this.message('Введіть щонайменше 2 символи назви міста.'); return;
        }
        const version = this.cityVersion;
        this.cityTimer = setTimeout(() => this.searchCities(version), 350);
      }, { signal });
      this.citySelect.addEventListener('change', () => this.chooseCity(), { signal });
      this.branchInput.addEventListener('input', () => {
        this.warehouseRef = ''; this.branchSelect.value = ''; this.branchVersion++;
        this.persistDraft();
        this.branchRequest?.abort();
        this.branchSelect.replaceChildren(new Option(TXT.branch, ''));
        this.showOptions('branch'); this.more.hidden = true;
        clearTimeout(this.branchTimer);
        this.branchTimer = setTimeout(() => this.loadBranches(false), 350);
      }, { signal });
      this.branchSelect.addEventListener('change', () => this.chooseBranch(), { signal });
      this.more.addEventListener('click', () => this.loadBranches(true), { signal });
      this.querySelector('[data-np-retry]')?.addEventListener('click', () => {
        if (this.city) this.loadBranches(false); else this.searchCities(this.cityVersion);
      }, { signal });
      // No cart-token lookup: choices are persisted immediately, including before any cart request.
      // An empty newly rendered widget must never overwrite a saved delivery preference.
      // Gate is installed by this asset; buttons remain gated through click/submit handling.
      if (!pending && !activeCartUpdates.size) document.querySelectorAll('[data-np-checkout]').forEach(button => { button.disabled = false; });
    }
    disconnectedCallback() {
      this.mountObserver?.disconnect(); this.mountObserver = null;
      this.cityVersion = (this.cityVersion || 0) + 1; this.branchVersion = (this.branchVersion || 0) + 1;
      this.life?.abort(); this.cityRequest?.abort(); this.branchRequest?.abort();
      clearTimeout(this.cityTimer); clearTimeout(this.branchTimer);
      cancelAnimationFrame(this.restoreFrame); this.restoreFrame = undefined;
      this.started = false;
    }
    message(text, error = false) {
      if (!this.status) return;
      this.status.textContent = text; this.status.dataset.error = String(error);
    }
    setupPicker(kind, input, select) {
      const list = this.querySelector(`[data-np-list="${kind}"]`);
      if (!list) return;
      select.hidden = true;
      const id = `veyro-np-${kind}-${++NPSelector.counter}`;
      list.id = id;
      input.setAttribute('role', 'combobox'); input.setAttribute('aria-autocomplete', 'list');
      input.setAttribute('aria-controls', id); input.setAttribute('aria-expanded', 'false');
      this[kind + 'Picker'] = { input, select, list, active: -1 };
      const signal = this.life.signal;
      input.addEventListener('focus', () => this.showOptions(kind), { signal });
      input.addEventListener('keydown', event => {
        const picker = this[kind + 'Picker'];
        const options = Array.from(list.children);
        if (event.key === 'Escape') { this.hideOptions(kind); return; }
        if (event.key === 'Tab') { this.hideOptions(kind); return; }
        if (event.key === 'Enter') {
          event.preventDefault();
          if (!list.hidden && picker.active >= 0) this.selectOption(kind, options[picker.active]?.dataset.value);
          return;
        }
        if (!['ArrowDown', 'ArrowUp'].includes(event.key) || !options.length) return;
        event.preventDefault(); list.hidden = false; input.setAttribute('aria-expanded', 'true');
        picker.active = event.key === 'ArrowDown' ? Math.min(options.length - 1, picker.active + 1) : Math.max(0, picker.active - 1);
        options.forEach((option, index) => option.setAttribute('aria-selected', String(index === picker.active)));
        input.setAttribute('aria-activedescendant', options[picker.active].id);
        options[picker.active].scrollIntoView({ block: 'nearest' });
      }, { signal });
      list.addEventListener('pointerdown', event => event.preventDefault(), { signal });
      list.addEventListener('click', event => {
        const option = event.target.closest('[data-value]');
        if (option && list.contains(option)) this.selectOption(kind, option.dataset.value);
      }, { signal });
      document.addEventListener('pointerdown', event => {
        if (!input.closest(".veyro-np-field").contains(event.target)) this.hideOptions(kind);
      }, { signal });
    }
    showOptions(kind) {
      const picker = this[kind + 'Picker']; if (!picker) return;
      const { list, input, select } = picker;
      const fragment = document.createDocumentFragment();
      for (const option of select.options) {
        if (!option.value) continue;
        const item = document.createElement('li');
        item.id = `${list.id}-${fragment.childNodes.length}`;
        item.setAttribute('role', 'option'); item.setAttribute('aria-selected', 'false');
        item.dataset.value = option.value; item.textContent = option.textContent;
        fragment.append(item);
      }
      list.replaceChildren(fragment); picker.active = -1;
      input.removeAttribute('aria-activedescendant');
      list.hidden = !list.children.length || document.activeElement !== input;
      input.setAttribute('aria-expanded', String(!list.hidden));
    }
    hideOptions(kind) {
      const picker = this[kind + 'Picker']; if (!picker) return;
      picker.list.hidden = true; picker.active = -1;
      picker.input.setAttribute('aria-expanded', 'false'); picker.input.removeAttribute('aria-activedescendant');
    }
    selectOption(kind, value) {
      const picker = this[kind + 'Picker']; if (!picker || !value) return;
      picker.select.value = value;
      picker.select.dispatchEvent(new Event('change', { bubbles: true }));
      this.hideOptions(kind);
    }
    chooseCity() {
      const option = this.citySelect.selectedOptions[0];
      this.cityVersion++; this.cityRequest?.abort();
      this.city = option?.value ? { ref: option.value, name: option.dataset.name || option.textContent } : null;
      this.resetBranches();
      if (this.city) {
        this.cityInput.value = option.textContent; this.branchInput.disabled = false;
        this.persistDraft();
        this.loadBranches(false);
      }
      this.hideOptions('city');
    }
    chooseBranch() {
      this.branchVersion++; this.branchRequest?.abort(); clearTimeout(this.branchTimer);
      this.more.disabled = false; this.branchInput.removeAttribute('aria-busy');
      this.warehouseRef = this.branchSelect.value;
      if (this.warehouseRef) this.branchInput.value = this.branchSelect.selectedOptions[0].textContent;
      this.hideOptions('branch');
      this.persistDraft();
      this.message(this.warehouseRef ? 'Відділення обрано. Перед оформленням перевіримо його доступність.' : TXT.branch);
    }
    resetBranches() {
      this.branchVersion++; this.branchRequest?.abort(); clearTimeout(this.branchTimer);
      this.warehouseRef = ''; this.page = 1; this.branchInput.value = ''; this.branchInput.disabled = true;
      this.branchSelect.replaceChildren(new Option('Спочатку оберіть місто', ''));
      this.branchSelect.disabled = true; this.more.hidden = true; this.more.disabled = false;
      this.hideOptions('branch');
      this.persistDraft();
    }
    async api(path, params, signal) {
      let url;
      try {
        const base = new URL(this.dataset.api);
        if (base.protocol !== 'https:') throw new Error();
        url = new URL(path, base);
      } catch { throw new Error('Не налаштовано сервіс доставки. Зверніться до магазину.'); }
      for (const [key, value] of Object.entries(params)) url.searchParams.set(key, value);
      return request(url, { credentials: 'omit' }, signal);
    }
    async searchCities(version) {
      const q = this.cityInput.value.trim(); if (q.length < 2) return;
      this.cityRequest?.abort(); this.cityRequest = new AbortController();
      this.message('Шукаємо місто…'); this.cityInput.setAttribute('aria-busy', 'true');
      try {
        const result = await this.api('/np/cities', { q }, this.cityRequest.signal);
        if (!this.isConnected || version !== this.cityVersion) return;
        if (!Array.isArray(result?.items) || result.items.some(item => typeof item?.ref !== 'string' || typeof item?.name !== 'string')) throw new Error(TXT.invalid);
        this.citySelect.replaceChildren(new Option(TXT.city, ''));
        for (const item of result.items) {
          const option = new Option(item.name + (item.area ? ' · ' + item.area : ''), item.ref);
          option.dataset.name = item.name; this.citySelect.add(option);
        }
        this.showOptions('city');
        this.message(result.items.length ? TXT.city : 'Місто не знайдено. Спробуйте уточнити назву українською.');
      } catch (error) {
        if (this.isConnected && version === this.cityVersion && error.name !== 'AbortError') this.message(error.message, true);
        else if (this.isConnected && version === this.cityVersion && !this.cityRequest.signal.aborted) this.message(TXT.unavailable, true);
      } finally { if (version === this.cityVersion) this.cityInput.removeAttribute('aria-busy'); }
    }
    async loadBranches(append) {
      if (!this.city || this.busy) return;
      const cityRef = this.city.ref, version = ++this.branchVersion;
      const page = append ? this.page + 1 : 1;
      this.branchRequest?.abort(); this.branchRequest = new AbortController();
      this.more.disabled = true; this.message('Шукаємо відділення…');
      this.branchInput.setAttribute('aria-busy', 'true');
      try {
        const result = await this.api('/np/warehouses', { city: cityRef, q: this.branchInput.value.trim(), page: String(page) }, this.branchRequest.signal);
        if (!this.isConnected || version !== this.branchVersion || this.city?.ref !== cityRef) return;
        if (!Array.isArray(result?.items) || result.items.some(item => typeof item?.ref !== 'string' || typeof item?.name !== 'string')) throw new Error(TXT.invalid);
        if (!append) {
          const selectedRef = this.warehouseRef;
          this.branchSelect.replaceChildren(new Option(TXT.branch, ''));
          if (selectedRef) this.branchSelect.add(new Option(this.branchInput.value, selectedRef, true, true));
        }
        const refs = new Set(Array.from(this.branchSelect.options, option => option.value));
        for (const item of result.items) if (!refs.has(item.ref)) { this.branchSelect.add(new Option(item.name, item.ref)); refs.add(item.ref); }
        this.branchSelect.disabled = false; this.page = page;
        this.more.hidden = result.hasMore !== true; this.showOptions('branch');
        this.message(this.branchSelect.options.length > 1 ? TXT.branch : 'Відділення не знайдено. Змініть запит або повторіть пошук.');
      } catch (error) {
        if (this.isConnected && version === this.branchVersion) this.message(error.name === 'AbortError' ? TXT.unavailable : error.message, true);
      } finally {
        if (version === this.branchVersion) { this.more.disabled = false; this.branchInput.removeAttribute('aria-busy'); }
      }
    }
    async saveForCheckout() {
      if (!this.city || !this.warehouseRef) {
        (this.city ? this.branchInput : this.cityInput).focus();
        throw new Error('Оберіть місто та відділення Нової пошти зі списку.');
      }
      this.persistDraft();
      const cityRef = this.city.ref, branchRef = this.warehouseRef;
      const life = this.life;
      const isCurrent = () => this.isConnected && this.life === life && !life.signal.aborted && this.city?.ref === cityRef && this.warehouseRef === branchRef;
      if (!isCurrent()) throw new Error('Вибір змінився. Перевірте дані та повторіть перехід.');
      this.message('Перевіряємо відділення…');
      const result = await this.api('/np/verify', { city: cityRef, ref: branchRef }, life.signal);
      const item = result?.item;
      if (result?.valid !== true || !item || item.ref !== branchRef || item.cityRef !== cityRef || typeof item.name !== 'string' || !item.name.trim() || typeof item.cityName !== 'string' || !item.cityName.trim() || typeof result.checkedAt !== 'string' || !result.checkedAt.trim() || (item.settlementRef != null && typeof item.settlementRef !== 'string')) throw new Error(TXT.invalid);
      if (!isCurrent()) throw new Error('Вибір змінився. Перевірте дані та повторіть перехід.');
      const payload = { attributes: {
        np_city_ref: item.cityRef, np_city_name: item.cityName, np_settlement_ref: item.settlementRef || '',
        np_warehouse_ref: item.ref, np_warehouse_name: item.name, np_delivery_payer: 'Recipient', np_checked_at: result.checkedAt
      }};
      const note = this.closest('dialog, main')?.querySelector('textarea[name="note"]');
      if (note) payload.note = note.value;
      let cart;
      try {
        cart = await request(root() + 'cart/update.js', {
          method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, body: JSON.stringify(payload)
        }, life.signal);
      } catch { throw new Error('Не вдалося підтвердити збереження доставки. Дані могли зберегтися. Перевірте кошик перед продовженням.'); }
      if (!isCurrent()) throw new Error('Кошик або вибір доставки оновився. Дані могли зберегтися. Перевірте кошик перед продовженням.');
      const attributesMatch = Object.entries(payload.attributes).every(([key, value]) => (cart?.attributes?.[key] ?? '') === value);
      const noteMatches = !Object.hasOwn(payload, 'note') || (cart?.note ?? '') === payload.note;
      if (!Number.isInteger(cart?.item_count) || cart.item_count < 1 || !attributesMatch || !noteMatches) throw new Error('Не вдалося підтвердити дані доставки. Перевірте кошик.');
      this.city = { ref: item.cityRef, name: item.cityName };
      this.cityInput.value = item.cityName;
      this.branchInput.value = item.name;
      this.dataset.cityRef = item.cityRef; this.dataset.cityName = item.cityName;
      this.dataset.warehouseRef = item.ref; this.dataset.warehouseName = item.name;
      this.persistDraft();
      this.message('Дані доставки збережено. Відкриваємо оформлення…');
    }
  }
  NPSelector.counter = 0;
  if (!customElements.get(TAG)) customElements.define(TAG, NPSelector);
  if (window.veyroNPGateInstalled) return;
  window.veyroNPGateInstalled = true;
  // The address subtree is preserved, but native cart morphs can replace checkout buttons.
  // Reconcile only after native cart work, without restoring/rewriting delivery fields.
  document.addEventListener('shopify:cart:lines-update', event => {
    const promise = event.promise;
    if (!promise || typeof promise.then !== 'function') { scheduleCheckoutSync(); return; }
    if (activeCartUpdates.has(promise)) return;
    activeCartUpdates.add(promise);
    document.querySelectorAll('[data-np-checkout]').forEach(button => { button.disabled = true; });
    const finish = () => { activeCartUpdates.delete(promise); scheduleCheckoutSync(); };
    Promise.resolve(promise).then(finish, finish);
  });
  const checkoutObserver = new MutationObserver(records => {
    for (const record of records) {
      if (record.type === 'attributes' && record.target.matches('[data-np-checkout]') && record.target.disabled) { scheduleCheckoutSync(); return; }
      for (const node of record.addedNodes || []) {
        if (node instanceof Element && (node.matches('[data-np-checkout]') || node.querySelector('[data-np-checkout]'))) { scheduleCheckoutSync(); return; }
      }
    }
  });
  const observeCheckout = () => {
    if (document.body) checkoutObserver.observe(document.body, { childList:true, subtree:true, attributes:true, attributeFilter:['disabled'] });
    scheduleCheckoutSync();
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', observeCheckout, {once:true});
  else observeCheckout();
  window.addEventListener?.('pageshow', scheduleCheckoutSync, {});
  async function proceed(trigger) {
    if (pending || activeCartUpdates.size) return;
    const scope = trigger?.closest('dialog, main');
    const widgets = Array.from((scope || document).querySelectorAll(TAG));
    // Never reuse an address from a hidden drawer or another cart surface.
    const widget = widgets.find(item => item.getClientRects().length && item.started);
    if (!widget) { window.location.assign(root() + 'cart'); return; }
    if (!widget.city || !widget.warehouseRef) {
      widget.message('Оберіть місто та відділення Нової пошти зі списку.', true);
      (widget.city ? widget.branchInput : widget.cityInput).focus(); return;
    }
    pending = true; widget.busy = true;
    widget.cityVersion++; widget.branchVersion++; widget.cityRequest?.abort(); widget.branchRequest?.abort();
    clearTimeout(widget.cityTimer); clearTimeout(widget.branchTimer);
    widget.hideOptions('city'); widget.hideOptions('branch');
    const controls = Array.from(widget.querySelectorAll('input, select, button')).map(element => ({ element, disabled: element.disabled }));
    const checkoutButtons = Array.from(document.querySelectorAll('[data-np-checkout],button[name="checkout"]')).map(element => ({ element, disabled: element.disabled }));
    [...controls, ...checkoutButtons].forEach(({ element }) => { element.disabled = true; });
    const life = widget.life;
    try {
      await widget.saveForCheckout();
      if (!widget.isConnected || widget.life !== life || life.signal.aborted) throw new Error('Кошик оновився. Перевірте дані перед продовженням.');
      window.location.assign(root() + 'checkout');
    }
    catch (error) { widget.message(error.name === 'AbortError' ? TXT.unavailable : error.message, true); widget.scrollIntoView({ behavior: 'auto', block: 'center' }); }
    finally {
      [...controls, ...checkoutButtons].forEach(({ element, disabled }) => { element.disabled = disabled; });
      pending = false; widget.busy = false;
      scheduleCheckoutSync();
    }
  }
  document.addEventListener('click', event => {
    if (!(event.target instanceof Element)) return;
    const target = event.target.closest('[name="checkout"],a[href]');
    if (!target || target.disabled) return;
    if (target.matches('a')) {
      let url; try { url = new URL(target.href, location.href); } catch { return; }
      const path = root() + 'checkout';
      if (url.origin !== location.origin || ![path, path + '/'].includes(url.pathname)) return;
    }
    event.preventDefault(); event.stopImmediatePropagation(); proceed(target);
  }, true);
  document.addEventListener('submit', event => {
    if (event.submitter?.name !== 'checkout') return;
    event.preventDefault(); event.stopImmediatePropagation(); proceed(event.submitter);
  }, true);
})();
